import { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Search, X, Calendar, MapPin, Users, ChevronLeft, ChevronRight, Download,
  Share2, Heart, Info, Grid3x3, ImageIcon, ArrowUpRight,
} from "lucide-react";
import api, { BACKEND } from "@/lib/api";

const resolve = (u) => (!u ? "" : u.startsWith("http") ? u : `${BACKEND}${u}`);

const FAV_KEY = "edulift.gallery.favs.v1";
const readFavs = () => {
  try { return new Set(JSON.parse(localStorage.getItem(FAV_KEY) || "[]")); } catch { return new Set(); }
};
const writeFavs = (set) => localStorage.setItem(FAV_KEY, JSON.stringify(Array.from(set)));

const DATE_FILTERS = [
  { key: "all", label: "All time" },
  { key: "7d", label: "Last 7 days" },
  { key: "30d", label: "Last 30 days" },
  { key: "90d", label: "Last 90 days" },
  { key: "1y", label: "Last year" },
];

const passesDateFilter = (dateStr, key) => {
  if (key === "all") return true;
  const d = new Date(dateStr);
  if (isNaN(d)) return true;
  const now = Date.now();
  const days = { "7d": 7, "30d": 30, "90d": 90, "1y": 365 }[key];
  return (now - d.getTime()) <= days * 86400 * 1000;
};

const PAGE_SIZE = 24;

const SkeletonMasonry = () => (
  <div className="[column-fill:_balance] columns-2 md:columns-3 lg:columns-4 gap-3" data-testid="gallery-skeleton">
    {Array.from({ length: 12 }).map((_, i) => (
      <div key={i} className="mb-3 break-inside-avoid rounded-xl bg-gray-100 animate-pulse" style={{ height: 140 + (i * 37) % 220 }} />
    ))}
  </div>
);

export default function Gallery() {
  const [albums, setAlbums] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [albumFilter, setAlbumFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("all");
  const [visible, setVisible] = useState(PAGE_SIZE);
  const [favs, setFavs] = useState(() => readFavs());
  const [lightboxIndex, setLightboxIndex] = useState(-1);
  const sentinelRef = useRef(null);

  useEffect(() => {
    api.get("/gallery")
      .then((r) => setAlbums(r.data || []))
      .catch(() => setAlbums([]))
      .finally(() => setLoading(false));
  }, []);

  // Flatten albums → photos, each carrying album context
  const allPhotos = useMemo(() => {
    const list = [];
    albums.forEach((a) => {
      (a.photos || []).forEach((url, i) => {
        list.push({
          id: `${a.id}-${i}`,
          url: resolve(url),
          rawUrl: url,
          album_id: a.id,
          album: a,
          caption: a.training_name,
          alt: `${a.training_name} · ${a.school_name}`,
          date: a.date,
          location: a.location,
          created_at: a.created_at,
        });
      });
    });
    return list;
  }, [albums]);

  const filteredPhotos = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return allPhotos.filter((p) => {
      if (albumFilter !== "all" && albumFilter !== "favs" && p.album_id !== albumFilter) return false;
      if (albumFilter === "favs" && !favs.has(p.id)) return false;
      if (!passesDateFilter(p.created_at || p.date, dateFilter)) return false;
      if (!needle) return true;
      return [p.album.school_name, p.album.training_name, p.album.location]
        .some((v) => v && v.toLowerCase().includes(needle));
    });
  }, [allPhotos, q, albumFilter, dateFilter, favs]);

  const visiblePhotos = useMemo(() => filteredPhotos.slice(0, visible), [filteredPhotos, visible]);
  const hasMore = visible < filteredPhotos.length;

  // Infinite scroll via IntersectionObserver
  useEffect(() => {
    if (!hasMore) return;
    const el = sentinelRef.current;
    if (!el) return;
    const obs = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) setVisible((v) => v + PAGE_SIZE);
    }, { rootMargin: "600px" });
    obs.observe(el);
    return () => obs.disconnect();
  }, [hasMore, visible, filteredPhotos.length]);

  const openLightbox = useCallback((photo) => {
    const idx = filteredPhotos.findIndex((p) => p.id === photo.id);
    if (idx >= 0) setLightboxIndex(idx);
  }, [filteredPhotos]);

  const closeLightbox = useCallback(() => setLightboxIndex(-1), []);
  const prev = useCallback(() => setLightboxIndex((i) => (i > 0 ? i - 1 : filteredPhotos.length - 1)), [filteredPhotos.length]);
  const next = useCallback(() => setLightboxIndex((i) => (i < filteredPhotos.length - 1 ? i + 1 : 0)), [filteredPhotos.length]);

  const toggleFav = useCallback((id) => {
    setFavs((prevSet) => {
      const s = new Set(prevSet);
      if (s.has(id)) s.delete(id); else s.add(id);
      writeFavs(s);
      return s;
    });
  }, []);

  // Keyboard handlers for lightbox
  useEffect(() => {
    if (lightboxIndex < 0) return;
    const onKey = (e) => {
      if (e.key === "Escape") closeLightbox();
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "f" || e.key === "F") {
        const p = filteredPhotos[lightboxIndex];
        if (p) toggleFav(p.id);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightboxIndex, next, prev, closeLightbox, toggleFav, filteredPhotos]);

  // Reset pagination on filter change
  useEffect(() => { setVisible(PAGE_SIZE); }, [q, albumFilter, dateFilter]);

  const activePhoto = lightboxIndex >= 0 ? filteredPhotos[lightboxIndex] : null;

  return (
    <div className="min-h-screen bg-[#FAFAFB]">
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-14">
        {/* Hero */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          className="max-w-3xl"
        >
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#4F46E5] mb-3">Gallery</div>
          <h1 className="font-display text-5xl sm:text-6xl tracking-tight font-semibold">Moments from the room.</h1>
          <p className="mt-4 text-lg text-gray-600 max-w-2xl">
            Every training, captured. {allPhotos.length.toLocaleString()} photos across {albums.length} albums from schools across the country.
          </p>
        </motion.div>

        {/* Search + Filters */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15, duration: 0.5 }}
          className="mt-10 flex flex-col gap-4"
          data-testid="gallery-toolbar"
        >
          <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
            <div className="relative flex-1 max-w-md">
              <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
              <input
                type="text"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search schools, trainings, cities…"
                data-testid="gallery-search"
                className="w-full pl-10 pr-9 py-3 rounded-full bg-white border border-gray-200 text-sm placeholder:text-gray-400 focus:outline-none focus:border-[#4F46E5] focus:ring-2 focus:ring-[#4F46E5]/10 transition-all"
              />
              {q && (
                <button
                  onClick={() => setQ("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 grid place-items-center rounded-full bg-gray-100 hover:bg-gray-200 text-gray-500"
                  aria-label="Clear search"
                  data-testid="gallery-search-clear"
                >
                  <X className="w-3 h-3" />
                </button>
              )}
            </div>

            <div className="relative">
              <select
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
                data-testid="gallery-date-filter"
                className="appearance-none pl-10 pr-9 py-3 rounded-full bg-white border border-gray-200 text-sm font-medium text-gray-700 focus:outline-none focus:border-[#4F46E5] focus:ring-2 focus:ring-[#4F46E5]/10 cursor-pointer"
              >
                {DATE_FILTERS.map((d) => <option key={d.key} value={d.key}>{d.label}</option>)}
              </select>
              <Calendar className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            </div>

            <div className="text-xs text-gray-500 font-medium sm:ml-auto tabular-nums" data-testid="gallery-count">
              {filteredPhotos.length.toLocaleString()} photo{filteredPhotos.length === 1 ? "" : "s"}
            </div>
          </div>

          {/* Filter chips */}
          <div className="flex gap-2 overflow-x-auto -mx-1 px-1 pb-1 scrollbar-hide">
            {[
              { key: "all", label: "All photos", icon: Grid3x3 },
              { key: "favs", label: `Favorites${favs.size ? ` · ${favs.size}` : ""}`, icon: Heart },
              ...albums.map((a) => ({ key: a.id, label: a.school_name, icon: null, meta: a.training_name })),
            ].map((chip) => {
              const active = albumFilter === chip.key;
              const Icon = chip.icon;
              return (
                <button
                  key={chip.key}
                  onClick={() => setAlbumFilter(chip.key)}
                  data-testid={`chip-${chip.key}`}
                  className={`shrink-0 inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all border ${
                    active
                      ? "bg-[#0F172A] text-white border-[#0F172A] shadow-sm"
                      : "bg-white text-gray-700 border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                  }`}
                >
                  {Icon && <Icon className="w-3.5 h-3.5" />}
                  <span>{chip.label}</span>
                  {chip.meta && <span className={`text-[10px] ${active ? "text-white/60" : "text-gray-400"} hidden sm:inline`}>· {chip.meta}</span>}
                </button>
              );
            })}
          </div>
        </motion.div>

        {/* Masonry */}
        <div className="mt-8">
          {loading ? (
            <SkeletonMasonry />
          ) : filteredPhotos.length === 0 ? (
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }}
              className="py-20 flex flex-col items-center text-center"
              data-testid="gallery-empty"
            >
              <div className="w-16 h-16 rounded-full bg-white border border-gray-200 grid place-items-center mb-4">
                <ImageIcon className="w-6 h-6 text-gray-400" />
              </div>
              <div className="font-display text-xl font-medium">No photos match</div>
              <div className="text-sm text-gray-500 mt-1 max-w-sm">
                Try clearing your search, choosing a different album, or expanding the date range.
              </div>
              <button
                onClick={() => { setQ(""); setAlbumFilter("all"); setDateFilter("all"); }}
                className="mt-5 px-4 py-2 rounded-full bg-[#0F172A] text-white text-sm font-medium hover:bg-[#0F172A]/90 transition-colors"
                data-testid="gallery-reset"
              >
                Reset filters
              </button>
            </motion.div>
          ) : (
            <>
              <div className="[column-fill:_balance] columns-2 md:columns-3 lg:columns-4 gap-3" data-testid="gallery-masonry">
                {visiblePhotos.map((p, i) => (
                  <motion.button
                    key={p.id}
                    layoutId={`photo-${p.id}`}
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: Math.min(i * 0.02, 0.4), duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                    whileHover={{ y: -2 }}
                    onClick={() => openLightbox(p)}
                    data-testid={`photo-${p.id}`}
                    aria-label={p.alt}
                    className="group relative mb-3 block w-full break-inside-avoid rounded-xl overflow-hidden bg-gray-100 cursor-zoom-in focus:outline-none focus-visible:ring-2 focus-visible:ring-[#4F46E5] focus-visible:ring-offset-2"
                  >
                    <img
                      src={p.url}
                      alt={p.alt}
                      loading="lazy"
                      decoding="async"
                      className="w-full h-auto object-cover transition-transform duration-500 group-hover:scale-[1.04]"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/0 to-black/0 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none" />
                    <div className="absolute bottom-0 left-0 right-0 p-3 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
                      <div className="text-xs font-semibold truncate">{p.album.school_name}</div>
                      <div className="text-[10px] text-white/70 truncate">{p.album.training_name} · {p.date}</div>
                    </div>
                    <button
                      onClick={(e) => { e.stopPropagation(); toggleFav(p.id); }}
                      className={`absolute top-2 right-2 w-8 h-8 grid place-items-center rounded-full transition-all ${
                        favs.has(p.id)
                          ? "bg-white/95 text-rose-500 opacity-100"
                          : "bg-white/85 text-gray-700 opacity-0 group-hover:opacity-100 hover:bg-white"
                      }`}
                      aria-label={favs.has(p.id) ? "Unfavorite" : "Favorite"}
                      data-testid={`fav-${p.id}`}
                    >
                      <Heart className={`w-4 h-4 ${favs.has(p.id) ? "fill-current" : ""}`} />
                    </button>
                  </motion.button>
                ))}
              </div>

              {hasMore && (
                <div ref={sentinelRef} className="py-10 grid place-items-center">
                  <div className="w-6 h-6 rounded-full border-2 border-gray-200 border-t-[#4F46E5] animate-spin" data-testid="gallery-load-more-spinner" />
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {activePhoto && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-50 bg-[#0F172A]/95 backdrop-blur-md flex flex-col lg:flex-row"
            onClick={closeLightbox}
            data-testid="gallery-lightbox"
          >
            {/* Top bar */}
            <div className="absolute top-0 left-0 right-0 z-20 flex items-center justify-between px-4 sm:px-6 py-4 text-white">
              <div className="text-xs font-mono opacity-70">
                {lightboxIndex + 1} / {filteredPhotos.length}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={(e) => { e.stopPropagation(); toggleFav(activePhoto.id); }}
                  className={`w-10 h-10 grid place-items-center rounded-full hover:bg-white/10 transition-colors ${favs.has(activePhoto.id) ? "text-rose-400" : "text-white"}`}
                  aria-label="Favorite"
                  data-testid="lightbox-fav"
                >
                  <Heart className={`w-5 h-5 ${favs.has(activePhoto.id) ? "fill-current" : ""}`} />
                </button>
                <a
                  href={activePhoto.url}
                  download
                  target="_blank"
                  rel="noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="w-10 h-10 grid place-items-center rounded-full hover:bg-white/10 text-white transition-colors"
                  aria-label="Download"
                  data-testid="lightbox-download"
                >
                  <Download className="w-5 h-5" />
                </a>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    if (navigator.share) {
                      navigator.share({ title: activePhoto.alt, url: activePhoto.url }).catch(() => {});
                    } else if (navigator.clipboard) {
                      navigator.clipboard.writeText(activePhoto.url);
                    }
                  }}
                  className="w-10 h-10 grid place-items-center rounded-full hover:bg-white/10 text-white transition-colors"
                  aria-label="Share"
                  data-testid="lightbox-share"
                >
                  <Share2 className="w-5 h-5" />
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); closeLightbox(); }}
                  className="w-10 h-10 grid place-items-center rounded-full hover:bg-white/10 text-white transition-colors"
                  aria-label="Close"
                  data-testid="lightbox-close"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Image area */}
            <div className="flex-1 flex items-center justify-center relative min-h-0 p-4 pt-16 pb-16 lg:pb-4" onClick={closeLightbox}>
              <button
                onClick={(e) => { e.stopPropagation(); prev(); }}
                className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 w-11 h-11 grid place-items-center rounded-full bg-black/40 hover:bg-black/60 text-white transition-colors z-10"
                aria-label="Previous"
                data-testid="lightbox-prev"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>

              <AnimatePresence mode="wait">
                <motion.img
                  key={activePhoto.id}
                  initial={{ opacity: 0, scale: 0.98 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.98 }}
                  transition={{ duration: 0.2 }}
                  src={activePhoto.url}
                  alt={activePhoto.alt}
                  onClick={(e) => e.stopPropagation()}
                  className="max-w-full max-h-full object-contain rounded-lg shadow-2xl cursor-default"
                  data-testid="lightbox-image"
                />
              </AnimatePresence>

              <button
                onClick={(e) => { e.stopPropagation(); next(); }}
                className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 w-11 h-11 grid place-items-center rounded-full bg-black/40 hover:bg-black/60 text-white transition-colors z-10"
                aria-label="Next"
                data-testid="lightbox-next"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>

            {/* Side / bottom info panel */}
            <motion.aside
              initial={{ opacity: 0, x: 32 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1, duration: 0.3 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full lg:w-[360px] lg:h-full bg-white lg:border-l lg:border-white/10 p-6 overflow-y-auto shrink-0"
              data-testid="lightbox-panel"
            >
              <div className="flex items-center gap-2 text-xs text-gray-500 mb-3">
                <Info className="w-3.5 h-3.5" /> Photo details
              </div>
              <div className="font-display text-xl font-semibold text-[#0F172A] leading-tight">{activePhoto.album.school_name}</div>
              <div className="text-sm text-[#4F46E5] font-medium mt-1">{activePhoto.album.training_name}</div>

              <div className="mt-5 space-y-3 text-sm text-gray-700">
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 grid place-items-center rounded-lg bg-indigo-50 text-indigo-600 shrink-0"><Calendar className="w-4 h-4" /></div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold">Date</div>
                    <div>{activePhoto.date || "—"}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 grid place-items-center rounded-lg bg-emerald-50 text-emerald-600 shrink-0"><MapPin className="w-4 h-4" /></div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold">Location</div>
                    <div>{activePhoto.location || "—"}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 grid place-items-center rounded-lg bg-amber-50 text-amber-600 shrink-0"><Users className="w-4 h-4" /></div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold">Teachers</div>
                    <div>{activePhoto.album.teacher_count || 0}</div>
                  </div>
                </div>
              </div>

              <div className="mt-6 pt-6 border-t border-gray-100">
                <div className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold mb-2">Caption</div>
                <div className="text-sm text-gray-700 leading-relaxed">
                  {activePhoto.caption} · captured at {activePhoto.album.school_name}, {activePhoto.location}.
                </div>
              </div>

              <div className="mt-6 pt-6 border-t border-gray-100">
                <Link
                  to={`/gallery/${activePhoto.album_id}`}
                  className="inline-flex items-center gap-1.5 text-sm font-medium text-[#4F46E5] hover:underline"
                  data-testid="lightbox-view-album"
                >
                  View full album <ArrowUpRight className="w-3.5 h-3.5" />
                </Link>
              </div>

              <div className="mt-6 pt-6 border-t border-gray-100 text-[10px] uppercase tracking-wider text-gray-400 font-semibold">
                Keyboard
              </div>
              <div className="mt-2 grid grid-cols-2 gap-1.5 text-[11px] text-gray-500">
                <div className="flex items-center gap-1.5"><kbd className="px-1.5 py-0.5 bg-gray-100 rounded font-mono">←</kbd><kbd className="px-1.5 py-0.5 bg-gray-100 rounded font-mono">→</kbd> Nav</div>
                <div className="flex items-center gap-1.5"><kbd className="px-1.5 py-0.5 bg-gray-100 rounded font-mono">F</kbd> Fav</div>
                <div className="flex items-center gap-1.5"><kbd className="px-1.5 py-0.5 bg-gray-100 rounded font-mono">ESC</kbd> Close</div>
              </div>
            </motion.aside>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
